// =============================================================================
// Walk — phone-first lot-walk workflow. Open it on your phone, walk the lot,
// tap a card per unit: "Looks good" records a quick check; "Log voltage"
// opens a number pad to capture the reading; "Skip" advances without writing.
//
// The queue is computed from the same risk partition the dashboard uses:
// at-risk first, then watch, then stale. So opening Walk gives you exactly
// the units that need eyes today, in priority order.
//
// Touch targets are intentionally huge — gloves-on usable. Progress bar at
// the top, end screen with a small celebration when the walk is done.
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import {
  useBatteryUnits, partitionByRisk, voltageBucket, daysSinceCheck, unitName, relativeWhen,
} from "../lib/useBatteryData.js";
import { recordCheckIn, authorFromProfile } from "../lib/data.js";

// Three pad modes — every action in the walk flow logs a real voltage.
//   "check"        → Looks good / Checked - OK   (or HV Check OK if HV)
//   "charge-start" → Charge / Charging           (or HV Charging if HV) + sets charging=true
//   "charge-end"   → End charge / Charge Complete + clears charging
// Action selection is done by intent + HV flag.
function actionForMode(unit, mode) {
  const isHv = unit?.voltageType === "HV";
  if (mode === "charge-start") return isHv ? "HV Charging" : "Charging";
  if (mode === "charge-end")   return "Charge Complete";
  return isHv ? "HV Check OK" : "Checked - OK";
}
function padHeadingFor(mode) {
  if (mode === "charge-start") return "Beginning voltage";
  if (mode === "charge-end")   return "Ending voltage";
  return "Voltage";
}
function toastTextFor(mode, v) {
  if (mode === "charge-start") return `Charge started at ${v.toFixed(2)}V.`;
  if (mode === "charge-end")   return `Charge ended at ${v.toFixed(2)}V.`;
  return `Logged ${v.toFixed(2)}V.`;
}

export default function Walk() {
  const nav = useNavigate();
  const { profile, user } = useAuth();
  const { data: units, loading } = useBatteryUnits();

  // Build the queue once per units snapshot.
  const queue = useMemo(() => {
    const { risk, warn, stale } = partitionByRisk(units);
    return [
      ...risk.map((u) => ({ ...u, _why: "At risk" })),
      ...warn.map((u) => ({ ...u, _why: "Watch" })),
      ...stale.map((u) => ({ ...u, _why: "Stale" })),
    ];
  }, [units]);

  const [idx, setIdx] = useState(0);
  const [skippedIds, setSkippedIds] = useState(() => new Set());
  const [doneIds, setDoneIds] = useState(() => new Set());
  // padMode = null | "check" | "charge-start" | "charge-end"
  const [padMode, setPadMode] = useState(null);
  const [pad, setPad] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Filter the queue down to "remaining" — anything not yet checked or skipped.
  const remaining = useMemo(
    () => queue.filter((u) => !skippedIds.has(u.id) && !doneIds.has(u.id)),
    [queue, skippedIds, doneIds]
  );
  const current = remaining[idx] || null;
  const total = queue.length;
  const completed = doneIds.size + skippedIds.size;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Toast auto-dismiss.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1800);
    return () => clearTimeout(t);
  }, [toast]);

  function next() {
    setPadMode(null);
    setPad("");
    setIdx(0); // we keep idx at 0 since `remaining` already excludes done/skipped
  }

  // Open the pad in a specific mode. The mode drives the heading, the action,
  // and the toast wording. Every action in the walk flow writes a voltage.
  function openPad(mode) {
    if (!current || submitting) return;
    setPadMode(mode);
    setPad("");
  }

  async function submitVoltage() {
    if (!current || submitting || !padMode) return;
    const v = Number(pad);
    if (!Number.isFinite(v) || v <= 0) {
      setToast({ kind: "error", text: "Enter a voltage." });
      return;
    }
    setSubmitting(true);
    const action = actionForMode(current, padMode);
    try {
      await recordCheckIn({
        unitId: current.id,
        voltage: v,
        action,
        by: authorFromProfile(profile),
      });
      setDoneIds((s) => new Set(s).add(current.id));
      setToast({ kind: "ok", text: toastTextFor(padMode, v) });
      next();
    } catch (err) {
      console.warn("[walk] submitVoltage failed:", err?.message);
      setToast({ kind: "error", text: err?.message || "Couldn't save." });
    } finally {
      setSubmitting(false);
    }
  }

  function skip() {
    if (!current) return;
    setSkippedIds((s) => new Set(s).add(current.id));
    setToast({ kind: "info", text: "Skipped." });
    next();
  }

  function padKey(k) {
    if (k === "back") { setPad((p) => p.slice(0, -1)); return; }
    if (k === ".")    { if (!pad.includes(".")) setPad((p) => p + "."); return; }
    setPad((p) => (p.length >= 5 ? p : p + k));
  }

  // -------- Render --------
  return (
    <main className="walk-stage">
      <header className="walk-bar">
        <Link to="/today" className="walk-back" aria-label="Back to Today">←</Link>
        <div className="walk-title">
          <div className="walk-eye">LOT WALK · {completed} / {total}</div>
          <div className="walk-progress">
            <div className="walk-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </header>

      {loading && <div className="walk-loading">Loading the queue…</div>}

      {/* Done state */}
      {!loading && total === 0 && (
        <section className="walk-done">
          <div className="walk-done-mark">
            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h1 className="walk-done-h1">Lot's <em>clean</em>.</h1>
          <p className="walk-done-sub">Nothing flagged on your floor right now. Enjoy your morning.</p>
          <Link to="/today" className="walk-btn primary">Back to Today</Link>
        </section>
      )}

      {!loading && total > 0 && !current && (
        <section className="walk-done">
          <div className="walk-done-mark">
            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <h1 className="walk-done-h1">Walk <em>done</em>.</h1>
          <p className="walk-done-sub">
            {doneIds.size} checked{skippedIds.size > 0 ? ` · ${skippedIds.size} skipped` : ""}.
          </p>
          <div className="walk-done-actions">
            <Link to="/today" className="walk-btn primary">Back to Today</Link>
            {skippedIds.size > 0 && (
              <button
                type="button"
                className="walk-btn ghost"
                onClick={() => { setSkippedIds(new Set()); setIdx(0); }}
              >
                Re-walk skipped
              </button>
            )}
          </div>
        </section>
      )}

      {/* Card flow */}
      {!loading && current && (
        <section className="walk-card">
          <div className="walk-why">
            <span className={`walk-pill${current._why === 'At risk' ? ' over' : current._why === 'Watch' ? ' warn' : ''}`}>{current._why}</span>
            <span className="walk-when">
              {(() => {
                const d = daysSinceCheck(current);
                return d === null ? "never checked" : d === 0 ? "checked today" : `${d}d since check`;
              })()}
            </span>
          </div>

          <h2 className="walk-name">{unitName(current)}</h2>
          <div className="walk-stock">{current.stockNumber || current.id.slice(0, 8)}</div>

          <div className="walk-voltage">
            {Number.isFinite(Number(current.voltage)) ? (
              <>
                <div className="walk-v-num" style={{ color: voltageColor(Number(current.voltage)) }}>
                  {Number(current.voltage).toFixed(2)}<span className="walk-v-unit">V</span>
                </div>
                <div className="walk-v-state">{stateLabel(voltageBucket(current))}</div>
              </>
            ) : (
              <div className="walk-v-num" style={{ color: "var(--text-dim, #6A6D74)" }}>—V</div>
            )}
          </div>

          {/* Charging state badge — when a unit is on the charger, the card
              tells the tech and only offers the end-charge action. */}
          {current.charging && (
            <div className="walk-charging-tag">
              <span className="walk-charging-dot" />
              On the charger
              {current.chargingStartedAt?.toDate && (
                <> · started {relativeWhen(current.chargingStartedAt)}</>
              )}
            </div>
          )}

          {!padMode && !current.charging && (
            <div className="walk-actions">
              <button type="button" className="walk-btn ok" onClick={() => openPad("check")} disabled={submitting}>
                <span className="walk-btn-tick">✓</span>
                Looks good
              </button>
              <button type="button" className="walk-btn primary" onClick={() => openPad("charge-start")} disabled={submitting}>
                <svg className="walk-btn-tick" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M13 2 L4 14 h7 l-2 8 9-12 h-7 z" />
                </svg>
                Charge
              </button>
              <button type="button" className="walk-btn ghost" onClick={skip} disabled={submitting}>
                Skip
              </button>
            </div>
          )}

          {!padMode && current.charging && (
            <div className="walk-actions">
              <button type="button" className="walk-btn ok" onClick={() => openPad("charge-end")} disabled={submitting}>
                <span className="walk-btn-tick">✓</span>
                End charge
              </button>
              <button type="button" className="walk-btn ghost" onClick={skip} disabled={submitting}>
                Skip
              </button>
            </div>
          )}

          {padMode && (
            <div className="walk-pad">
              <div className="walk-pad-heading">{padHeadingFor(padMode)}</div>
              <div className="walk-pad-display">
                <span>{pad || "0"}</span>
                <span className="walk-v-unit">V</span>
              </div>
              <div className="walk-pad-grid">
                {["1","2","3","4","5","6","7","8","9",".","0","back"].map((k) => (
                  <button key={k} type="button" className={`walk-pad-key${k === 'back' ? ' back' : ''}`} onClick={() => padKey(k)} disabled={submitting}>
                    {k === "back" ? "⌫" : k}
                  </button>
                ))}
              </div>
              <div className="walk-actions">
                <button type="button" className="walk-btn primary" onClick={submitVoltage} disabled={submitting || !pad}>
                  {submitting ? "Saving…" : padMode === "charge-start" ? `Start charge at ${pad || "—"}V` : padMode === "charge-end" ? `End charge at ${pad || "—"}V` : `Save ${pad || "—"}V`}
                </button>
                <button type="button" className="walk-btn ghost" onClick={() => { setPadMode(null); setPad(""); }} disabled={submitting}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="walk-meta">
            Up next: <b>{remaining.length - 1}</b> more
          </div>
        </section>
      )}

      {toast && (
        <div className={`walk-toast walk-toast-${toast.kind}`} role="status">{toast.text}</div>
      )}
    </main>
  );
}

function voltageColor(v) {
  if (!Number.isFinite(v)) return "var(--text-muted, #4A4D54)";
  if (v < 12.0) return "#7C2935";
  if (v < 12.4) return "#B05E13";
  return "#2A6D38";
}
function stateLabel(bucket) {
  switch (bucket) {
    case "ok":   return "Healthy";
    case "fair": return "OK";
    case "warn": return "Watch";
    case "risk": return "At risk";
    case "hv":   return "HV pack";
    default:     return "No reading";
  }
}
